// @ts-check
/**
 * src/api/storage-azure.js
 *
 * Azure-backed storage adapter — core state and platform operations.
 *
 * This file covers: tenant state, documents, users, registry, access requests,
 * framework catalogue, app version, and UI layout.
 *
 * Module-specific APIs live in their own files:
 *   riskApi.js           — Risk CRUD and governance lifecycle
 *   aiGovernanceApi.js   — AI Governance (EU AI Act)
 *   governanceRolesApi.js — Governance RBAC
 *   trainingApi.js       — Global AI training pairs (super-admin)
 *   securityApi.js       — Breach findings
 *   grcApi.js            — Auditor access grants, assessment reminders
 *   vendorRegistryApi.js — Vendor registry lookups + assessment portal
 *
 * Architecture:
 *   Browser → Azure Functions (API) → Azure SQL
 *
 * Environment routing (super-admin cross-environment management):
 *   Every platform operation below takes an optional trailing options object
 *   carrying `env`. Tenant callers omit it and hit this deployment's own
 *   Function App with the tenant resolved from the JWT. The super-admin portal
 *   passes `env` ('production' | 'test' | 'development') to target another
 *   environment's Function App, and names the tenant explicitly because a
 *   super-admin JWT carries no tenant_id. That routing is the only delta —
 *   there is no separate env-parameterized copy of this module.
 */

import { azureGet, azurePost, azureEnvGet, azureEnvPost } from './azureClient.js';
import { safeRead } from './storageRequestHelpers.js';
import { adoptEmployeeVersions } from '../state/employeeVersions.js';

/**
 * The tenant state blob, as persisted in `tenant_state.state`.
 * @typedef {import('../state/types.js').StateShape} StateShape
 */

/**
 * A record whose shape is owned by the server (an Azure Function response
 * body) and is not mirrored as a frontend typedef. Deliberately permissive —
 * these are JSON documents assembled by the corresponding Azure Function.
 * @typedef {Record<string, any>} ApiRecord
 */

/**
 * Response body shared by the blob-upload endpoints
 * (`supplier-evidence-upload`, `agreements-upload`).
 * @typedef {{ ok: boolean, blobName: string }} BlobUploadResult
 */

/**
 * Routing options accepted by every platform operation in this module.
 *
 * @typedef {object} EnvOptions
 * @property {import('./azureClient.js').AzureEnvironment} [env] - target another
 *   environment's Function App (super-admin only). Omit for the current tenant.
 * @property {string} [tenantId] - explicit tenant for cross-env calls whose
 *   primary argument is not the tenant id (e.g. setUserPassword).
 * @property {AbortSignal} [signal] - aborts a non-env read.
 * @property {boolean} [keepalive] - lets a final non-env write survive unload.
 * @property {boolean} [silent] - opts a background/best-effort non-env call out
 *   of the generic error banner and auto-report (see azureClient.js
 *   surfaceApiError) — for calls whose own caller already swallows the
 *   failure and never shows the user anything, e.g. a heartbeat/poll.
 */

// ── Registry row ID (environment-specific) ────────────────────────────────────
const REGISTRY_ID = import.meta.env.VITE_REGISTRY_ID || 'registry';

/** @type {Record<string, string>} */
const ENV_REGISTRY_IDS = {
  production: 'registry',
  test: 'registry-test',
  development: 'registry-development',
};

/**
 * The registry row to read/write: this build's row by default, or the target
 * environment's row when the super-admin portal is working cross-env.
 *
 * @param {string} [env]
 * @returns {string}
 */
function registryIdFor(env) {
  return env ? ENV_REGISTRY_IDS[env] || 'registry' : REGISTRY_ID;
}

/**
 * GET router — the current environment via azureGet, or a named environment's
 * Function App via azureEnvGet.
 *
 * @param {string} path
 * @param {Record<string, string | number | boolean | null | undefined>} [params]
 * @param {EnvOptions} [opts]
 * @returns {Promise<any>}
 */
/**
 * In-flight GET coalescing — two callers asking for the SAME thing at the same
 * moment share one request.
 *
 * Measured on a page load: `readAppVersion()` and `readUiLayout()` both issue
 * `/api/registry-read?registryId=…` with no lookup params, in the same
 * millisecond, and each pulls the whole registry response to read one scalar
 * off it (`app_version`, `ui_layout`). Two byte-identical requests for two
 * fields of one answer.
 *
 * That matters more than its size suggests. Azure SQL bills concurrency in
 * *workers* — queries executing at once — so every duplicated request is a
 * worker consumed for nothing, against a ceiling the whole database shares
 * (see `docs/scaling-plan.md`). A duplicate is the cheapest worker there is
 * to give back.
 *
 * Done here rather than at the two call sites on purpose. Teaching two
 * functions to cooperate fixes those two functions; the next pair that
 * reads a second field off the same endpoint reintroduces it, and nothing
 * would catch that. Coalescing the transport makes every future duplicate
 * free without anyone having to know about this.
 *
 * NOT A CACHE. An entry lives only while its request is in flight and is
 * dropped the moment it settles, so a caller can never be handed a stale
 * answer — only an answer that is already on its way.
 *
 * Three things are deliberately never shared:
 *   - Anything but a GET. A POST is not idempotent and two of them are two
 *     intended writes.
 *   - A request carrying an AbortSignal. Those are abortable by design, and
 *     one caller aborting must never cancel another caller's request.
 *   - A cross-environment read (`opts.env`), which goes to a different
 *     Function App entirely.
 * `silent` is part of the key rather than ignored, because two callers that
 * disagree about surfacing errors must not inherit each other's choice.
 */
const inFlightGets = new Map();

function inFlightKey(path, params, silent) {
  const stable = Object.keys(params)
    .sort()
    .map((k) => `${k}=${String(params[k])}`)
    .join('&');
  return `${silent ? 's' : 'l'} ${path}?${stable}`;
}

/**
 * A joiner must not receive the same object reference as the original caller —
 * one of them mutating it would silently corrupt the other's copy. Cloning
 * only happens when somebody actually joined, so the ordinary single-caller
 * request pays nothing. A value that cannot be structured-cloned is handed
 * back as-is: an aliased object is a hazard, a thrown error here would be a
 * broken page load.
 */
function copyForJoiners(value) {
  try {
    return structuredClone(value);
  } catch {
    return value;
  }
}

function apiGet(path, params = {}, opts = {}) {
  if (opts.env) return azureEnvGet(opts.env, path, params);

  const fetchOpts = {
    ...(opts.signal ? { signal: opts.signal } : {}),
    ...(opts.silent ? { silent: true } : {}),
  };
  if (opts.signal) return azureGet(path, params, fetchOpts);

  const key = inFlightKey(path, params, !!opts.silent);
  const existing = inFlightGets.get(key);
  if (existing) {
    existing.joined = true;
    return existing.promise.then(copyForJoiners);
  }

  const entry = { joined: false };
  entry.promise = azureGet(path, params, fetchOpts);
  inFlightGets.set(key, entry);
  const forget = () => {
    if (inFlightGets.get(key) === entry) inFlightGets.delete(key);
  };
  entry.promise.then(forget, forget);

  // The originator is cloned too whenever anybody joined, so neither side
  // holds the reference the other could mutate.
  return entry.promise.then((v) => (entry.joined ? copyForJoiners(v) : v));
}

/**
 * POST router — see {@link apiGet}.
 *
 * @param {string} path
 * @param {object} [body]
 * @param {EnvOptions} [opts]
 * @returns {Promise<any>}
 */
function apiPost(path, body = {}, opts = {}) {
  if (opts.env) return azureEnvPost(opts.env, path, body);
  return azurePost(path, body, {
    ...(opts.keepalive ? { keepalive: true } : {}),
    ...(opts.silent ? { silent: true } : {}),
  });
}

/**
 * Tenant targeting for a request payload.
 *
 * A tenant-scoped JWT resolves the tenant server-side, so the tenant id must
 * NOT be sent — sending it would be ignored at best and is not the contract.
 * A super-admin JWT carries no tenant_id, so cross-env calls must name the
 * tenant explicitly (resolveTenantId honours it only for super-admins).
 *
 * @param {string | undefined} tenantId
 * @param {EnvOptions} opts
 * @returns {{ tenantId?: string }}
 */
function tenantScope(tenantId, opts) {
  return opts.env ? { tenantId } : {};
}

// ── Tenant-scoped state ───────────────────────────────────────────────────────

/**
 * Read the current tenant's state blob. Never throws — logs and resolves to
 * `null` when the read fails.
 *
 * @param {string} _tenantId - unused; the tenant is resolved from the JWT server-side
 * @returns {Promise<StateShape | null>}
 */
export async function readTenantState(_tenantId) {
  return safeRead(
    'readTenantState',
    async () => (await azureGet('/api/state-read')).state ?? null,
    null
  );
}

/**
 * Persist the current tenant's state blob. Never throws — logs on failure.
 *
 * @param {string} tenantId - unused by the request; the tenant comes from the JWT
 * @param {StateShape} state
 * @returns {Promise<void>}
 */
export async function writeTenantState(tenantId, state) {
  await safeRead('writeTenantState', () => azurePost('/api/state-write', { state }), undefined);
}

// ── Strict variants — for admin operations that must surface errors ───────────

/**
 * Read the current tenant's state blob, propagating any error to the caller.
 *
 * @param {string} _tenantId - unused; the tenant is resolved from the JWT server-side
 * @returns {Promise<StateShape | null>}
 */
export async function readTenantStateStrict(_tenantId) {
  const data = await azureGet('/api/state-read');
  return data.state ?? null;
}

// ── Versioned variants — optimistic-locking write path ───────────────────────
// readTenantStateStrictWithVersion returns { state, stateVersion } where
// stateVersion is the hex ROWVERSION string from the DB.  Pass it back to
// writeTenantStateVersioned so the server can detect concurrent writes and
// return 409 instead of silently clobbering data.

/**
 * Read the tenant state together with its hex ROWVERSION.
 *
 * @param {string} tenantId - only sent when `env` is set; otherwise the tenant
 *   is resolved from the JWT server-side
 * @param {AbortSignal} [signal] - aborts the in-flight read (non-env calls;
 *   env calls carry their own request timeout)
 * @param {EnvOptions} [opts]
 * @returns {Promise<{ state: StateShape | null, stateVersion: string | null }>}
 */
export async function readTenantStateStrictWithVersion(tenantId, signal, opts = {}) {
  const data = await apiGet('/api/state-read', tenantScope(tenantId, opts), { ...opts, signal });
  // Per-employee ROWVERSIONs for dbo.tenant_employees (ADR-0008 §2.4). Adopted
  // here rather than returned, because every load path in usePersistence.js
  // funnels through this one call and the tokens belong to the server rows,
  // not to whichever load happened to fetch them. A super-admin read of
  // ANOTHER environment's tenant must not overwrite them — those versions
  // belong to a different database, and employees-write only ever writes the
  // signed-in tenant.
  if (!opts.env) adoptEmployeeVersions(data.employeeVersions);
  return { state: data.state ?? null, stateVersion: data.stateVersion ?? null };
}

// Returns the new hex ROWVERSION the server produced so the caller can keep
// its cached version in lock-step — without this the next write sends a stale
// version and gets a guaranteed 409 (self-conflict). null when the row was a
// no-op (absent) write.
/**
 * @param {string} tenantId - only sent when `env` is set; otherwise the tenant
 *   comes from the JWT
 * @param {StateShape} state
 * @param {string | null} [stateVersion] - hex ROWVERSION read alongside `state`
 * @param {EnvOptions & { blobKeysOwnedByStores?: string[], onNotPersisted?: (families: string[]) => void, onRewritten?: (rewritten: Record<string, { value: unknown, reasons: object[] }>) => void }} [options]
 *   `keepalive` lets a final write survive a tab close / page unload; `env`
 *   targets another environment's Function App; `blobKeysOwnedByStores`
 *   declares the per-record families this caller strips (GH #6148) and
 *   `onNotPersisted` receives the families the server dropped; `onRewritten`
 *   receives what the server stored for keys a guard changed
 * @returns {Promise<string | null>} the new hex ROWVERSION, or null on a no-op write
 */
export async function writeTenantStateVersioned(tenantId, state, stateVersion, options = {}) {
  const data = await apiPost(
    '/api/state-write',
    {
      ...tenantScope(tenantId, options),
      state,
      stateVersion,
      // GH #6148 — the families this caller strips itself, declared so the
      // endpoint REFUSES (409) rather than silently dropping them if a bug ever
      // lets one through. Omitted when the caller does not strip (the
      // super-admin cross-environment paths hand over whole snapshots), which
      // leaves the endpoint's reporting-only behaviour untouched.
      ...(options.blobKeysOwnedByStores
        ? { blobKeysOwnedByStores: options.blobKeysOwnedByStores }
        : {}),
    },
    options
  );
  // Advisory: key families state-write was handed and did not persist. Reported
  // through a callback rather than the return value so every existing caller —
  // all of which want the ROWVERSION and nothing else — is unaffected.
  if (Array.isArray(data?.notPersisted) && data.notPersisted.length && options.onNotPersisted) {
    try {
      options.onNotPersisted(data.notPersisted);
    } catch {
      /* a reporting callback must never break the save it is reporting on */
    }
  }
  // OPP004A-04: per guarded key, the value the server stored where it differs
  // from what was sent, and why (azure-functions/shared/state/rewritten.js).
  // The caller must adopt it as server truth instead of its own payload.
  if (data?.rewritten && typeof data.rewritten === 'object' && options.onRewritten) {
    try {
      options.onRewritten(data.rewritten);
    } catch {
      /* a reporting callback must never break the save it is reporting on */
    }
  }
  return data?.stateVersion ?? null;
}

/**
 * CONC-01 (GH #7817) — save a field-level patch of the tenant blob.
 *
 * `ops` come from azure-functions/shared/state/statePatch.cjs `diffState`:
 * only what this tab changed, each with the value it changed it from. The
 * server applies them to the CURRENT row, so another session's save in
 * between never refuses this one; a 409 now means one of these fields was
 * changed to something else (reason `field-conflict`, with `conflicts`).
 *
 * Returns the new hex ROWVERSION, like writeTenantStateVersioned.
 */
export async function writeTenantStatePatch(tenantId, ops, options = {}) {
  const data = await apiPost(
    '/api/state-write',
    { ...tenantScope(tenantId, options), patch: ops },
    options
  );
  if (Array.isArray(data?.notPersisted) && data.notPersisted.length && options.onNotPersisted) {
    try {
      options.onNotPersisted(data.notPersisted);
    } catch {
      /* a reporting callback must never break the save it is reporting on */
    }
  }
  if (data?.rewritten && typeof data.rewritten === 'object' && options.onRewritten) {
    try {
      options.onRewritten(data.rewritten);
    } catch {
      /* a reporting callback must never break the save it is reporting on */
    }
  }
  return data?.stateVersion ?? null;
}

// ── Per-document write serialization ──────────────────────────────────────────
// Two UI instances for the same document (e.g. a review modal closed and
// reopened while its in-flight submit hasn't resolved yet, or a fast
// double-click racing the disable-while-saving guard) can otherwise fire
// overlapping documents-write / documents-workflow requests for the same
// docId. The server-side optimistic-concurrency check correctly rejects the
// loser with a write conflict, but the caller that issued it has no way to
// have anticipated that.
//
// This used to refuse the second call outright (throw "already being saved").
// That silently dropped legitimate transitions: the editor's own debounced
// autosave (and its follow-up version-snapshot-blob-ref persist, fired from an
// unawaited `.then()`) could hold the lock at the exact moment a user clicked
// "Submit for Review", and the resulting throw either surfaced as a confusing
// error or — when it hit the unawaited background persist — was swallowed to
// a console.warn with no user-visible effect and no documents-workflow call
// ever sent. Queue instead of throwing: every call for the same docId still
// runs one at a time (removing the race at the source, same as before), but a
// call made while another is in flight now waits its turn and runs, rather
// than being rejected and lost.
/** @type {Map<string, Promise<any>>} */
const _docOpQueue = new Map();

/**
 * Run `fn` only after every previously queued operation for `docId` has
 * settled, serializing same-document writes without dropping any of them.
 * @template T
 * @param {string | null | undefined} docId
 * @param {() => Promise<T>} fn
 * @returns {Promise<T>}
 */
function runDocOp(docId, fn) {
  if (!docId) return fn();
  const prior = _docOpQueue.get(docId) || Promise.resolve();
  const run = prior.catch(() => {}).then(fn);
  _docOpQueue.set(docId, run);
  // `run` is returned below for the actual caller to await/catch (e.g. the
  // caller's own .catch(), or useDocumentState's snapshotBlob-ref follow-up
  // write). `.finally()` here returns a SEPARATE derived promise that adopts
  // the same rejection — left unhandled, it fires its own "unhandled promise
  // rejection" for the exact same error the caller already caught (e.g. a
  // documents-write 403 from a stale write racing a documents-workflow
  // transition). Swallow it — the caller's promise is the one that matters.
  run
    .finally(() => {
      if (_docOpQueue.get(docId) === run) _docOpQueue.delete(docId);
    })
    .catch(() => {});
  return run;
}

// ── Document-scoped write — one row in tenant_documents per doc ───────────────
// Used by saveDocument so the editor save uploads a single document (+ small
// sibling slices) instead of the whole tenant state. Phase 2 of the migration
// moved this path off the JSON_MODIFY-against-state column onto a dedicated
// per-document table — saves are bounded by document size, not tenant state
// size.
/**
 * @param {string} tenantId - unused by the request; the tenant comes from the JWT
 * @param {{ doc?: { id?: string } | null } & ApiRecord} payload - the document
 *   plus any sibling state slices the editor is persisting alongside it
 * @returns {Promise<any>} the `documents-write` response —
 *   `{ ok, docVersion, stateVersion? }`
 */
export async function writeDocument(tenantId, payload) {
  const docId = payload?.doc?.id;
  return runDocOp(docId, () => azurePost('/api/documents-write', payload));
}

// ── Document workflow transitions — status-machine endpoint ──────────────────
// Status changes must never go through documents-write (DOC-B-001). This
// function is the only frontend path to advance documentStatus.
//
// #5925: a status transition is a plain (non-keepalive) POST, so a real page
// unload while it is still in flight (closing the tab, a hard reload, a
// navigation racing the request) leaves its delivery undefined by the Fetch
// spec — the browser may drop it outright, or the server may keep processing
// an already-received request after the client has moved on and commit the
// transition invisibly, later. Either way the caller's own success/error
// handling (DocumentWorkflowHub's dispatchWorkflowFailure, #5248) never runs,
// because the page that would show it is already gone — the document's status
// can then read differently across two otherwise-unrelated reloads with no
// user action in between. The action-specific body here (docId + a handful of
// small fields) is always well under the browser's ~64 KiB keepalive quota
// (unlike the whole-tenant-state teardown write, GH #5819), so requesting
// keepalive here is unconditional and safe — it lets the write survive the
// unload instead of racing it.
/**
 * @param {string} action - the workflow transition (e.g. 'publish', 'approve')
 * @param {{ docId?: string } & ApiRecord} body - action-specific payload
 * @returns {Promise<any>} the `documents-workflow` response — shape is
 *   action-dependent (`{ ok, doc, stateVersion? }`)
 */
export async function callDocumentsWorkflow(action, body) {
  const docId = body?.docId;
  return runDocOp(docId, () =>
    azurePost(`/api/documents-workflow?action=${action}`, body, { keepalive: true })
  );
}

// ── Document readers — the one list of who must read (DOC-B-026) ─────────────
// Per-target add / remove on documents-assign. The server applies the delta
// inside the document row's compare-and-swap and re-applies it on conflict, so
// these never need a _docVersion and two admins editing different readers both
// land. Neither moves the document's version, text or acknowledgements; the
// response's docVersion is the row's new ROWVERSION.
/**
 * @param {string} documentId
 * @param {{ targets: Array<{ type: string, value?: string|null }>, deadline?: string|null,
 *   note?: string, source?: 'document'|'employee'|'share', sendEmail?: boolean }} body
 * @returns {Promise<any>} `{ ok, documentId, readerTargets, docVersion, added, updated,
 *   readersBefore, readersAfter, emailsSent, emailErrors, emailSkipped?, stateVersion? }`
 */
export async function addDocumentReaders(documentId, body) {
  return runDocOp(documentId, () =>
    azurePost('/api/documents-assign?action=add-readers', { ...body, documentId })
  );
}

/**
 * Change one reader target's deadline and/or note in place. Each changed field
 * carries the value the caller last saw (`expectedDeadline` / `expectedNote`);
 * if someone else changed that field meanwhile the server refuses with 409
 * ERR_READER_FIELD_CONFLICT and the thrown error's `conflicts` name each field
 * with both values (TRYGGR_INTENT §13).
 * @param {string} documentId
 * @param {{ targetId: string, deadline?: string|null, expectedDeadline?: string|null,
 *   note?: string, expectedNote?: string }} change
 * @returns {Promise<any>} `{ ok, documentId, readerTargets, docVersion, updated }`
 */
export async function updateDocumentReader(documentId, { targetId, ...fields }) {
  return runDocOp(documentId, () =>
    azurePost('/api/documents-assign?action=update', { id: targetId, ...fields })
  );
}

/**
 * Remove one reader target. A removal can turn read evidence green without
 * anybody reading, so the server requires a reason and the document owner or
 * an admin, and records both on the document and in the activity log.
 * @param {string} documentId
 * @param {string} targetId
 * @param {string} reason
 * @returns {Promise<any>} `{ ok, documentId, readerTargets, docVersion, alreadyRemoved,
 *   readersBefore, readersAfter }`
 */
export async function removeDocumentReader(documentId, targetId, reason) {
  return runDocOp(documentId, () =>
    azurePost('/api/documents-assign?action=remove-reader', { documentId, targetId, reason })
  );
}

/**
 * Add N employees as readers of M documents (the Employees list bulk assign).
 * Each document is one add-readers delta under its own row compare-and-swap;
 * departed, offboarding and unknown ids are skipped and reported (GH #8031).
 * @param {{ documentIds: string[], employeeIds: string[], deadline?: string|null,
 *   note?: string, sendEmail?: boolean }} body
 * @returns {Promise<any>} `{ ok, documents: [{ documentId, readerTargets, docVersion }],
 *   stateVersion, emailsSent, emailErrors, skipped: [{ employeeId, reason }] }`
 */
export async function bulkAssignDocumentReaders(body) {
  return azurePost('/api/documents-assign?action=bulk-assign', body);
}

// ── Document-scoped delete — row removal from tenant_documents ────────────────
// Complements the in-memory soft-delete in useAppState.deleteDocument: the
// debounced state-write persists state.deletedDocuments, this call removes
// the per-document row so the merge in state-read can never resurrect it.
/**
 * @param {string} tenantId - unused by the request; the tenant comes from the JWT
 * @param {string} docId
 * @returns {Promise<{ok: boolean, stateVersion?: string}>}
 */
export async function deleteDocumentRow(tenantId, docId) {
  return azurePost('/api/documents-delete', { docId });
}

// ── Document version snapshots — blob-backed archive ──────────────────────────
// The most recent N entries in versionHistory keep an inline contentSnapshot
// for instant View/Compare. Older entries store only a snapshotBlob ref that
// the editor lazy-fetches via these helpers when the user clicks View or
// Compare. Blob path is { tenantId }/_versions/<docId>/v<version>-<entryId>.json
// and is scoped by JWT tenantId on the server side.
//
// One blob per history ENTRY, never per version label (GH #8800). Many saves
// keep the same label (editor saves, the workflow hub, a regenerated report);
// a label-only name let every later save overwrite the snapshot an earlier
// entry points at, so version history showed text that entry never held.
// Entries written before this keep their stored `snapshotBlob` ref unchanged.
/**
 * @param {string | null | undefined} docId
 * @param {number | string | null | undefined} version
 * @param {any} snapshot - the JSON-serialisable version snapshot
 * @param {string | null | undefined} entryId - the history entry's own id
 * @returns {Promise<string | null>} the blob ref to store as
 *   `versionHistory[].snapshotBlob`, or null when nothing was uploaded
 */
export async function uploadDocumentVersionSnapshot(docId, version, snapshot, entryId) {
  // No entry id, no upload: a label-only name is exactly the collision above.
  if (!docId || version == null || !entryId) return null;
  const filename = `_versions/${encodeURIComponent(docId)}/v${encodeURIComponent(
    version
  )}-${encodeURIComponent(entryId)}.json`;
  const json = JSON.stringify(snapshot);
  // Browser-safe base64 encode for UTF-8 JSON
  const base64 =
    typeof btoa === 'function'
      ? btoa(unescape(encodeURIComponent(json)))
      : Buffer.from(json, 'utf8').toString('base64');
  await azurePost('/api/documents-upload', {
    filename,
    contentType: 'application/json',
    data: base64,
  });
  return filename;
}

/**
 * @param {string | null | undefined} snapshotBlob - blob ref produced by
 *   {@link uploadDocumentVersionSnapshot}
 * @returns {Promise<any>} the parsed snapshot JSON, or null when there is no ref
 */
export async function downloadDocumentVersionSnapshot(snapshotBlob) {
  if (!snapshotBlob) return null;
  const { url } = await azureGet('/api/documents-download', { filename: snapshotBlob });
  if (!url) return null;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Snapshot fetch failed: ${res.status}`);
  return res.json();
}

// ── Document image blobs — content-addressed ──────────────────────────────────
// An image inserted into a document used to be stored as a base64 data: URL
// inside the block JSON, which rides in the tenant_documents row that
// documents-write round-trips on every save and eats the 64 KiB keepalive
// teardown budget (azureClient.js's KEEPALIVE_BODY_BUDGET_BYTES). The bytes now
// live in the 'documents' blob container and the block carries a reference.
//
// The ref is CONTENT-ADDRESSED — `_images/<docId>/<sha256>.<ext>` — and not
// keyed on the block id, deliberately. DOC-B-007's segregation-of-duties
// fingerprint is JSON.stringify([title, content]), so it only keeps covering an
// image if different bytes produce a different ref and therefore different
// `content`. documents-upload re-derives the hash server-side and refuses a
// payload that does not match the path it claims, so the ref cannot lie.
//
// Uploading identical bytes twice is idempotent by construction: same content,
// same hash, same path.

/** @param {ArrayBuffer} buf @returns {Promise<string>} lowercase hex sha-256 */
async function sha256Hex(buf) {
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function base64FromBytes(bytes) {
  let binary = '';
  const CHUNK = 0x8000; // avoid blowing the argument limit on a large image
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

const IMAGE_EXT_BY_TYPE = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

/**
 * Store an image's bytes as a document-image blob and return the ref to put in
 * the block's `imageRef`.
 *
 * @param {string} docId
 * @param {ArrayBuffer} bytes  - the encoded image, exactly as it will be served
 * @param {string} contentType - one of IMAGE_EXT_BY_TYPE's keys
 * @returns {Promise<string>} `_images/<docId>/<sha256>.<ext>`
 * @throws when the type is unsupported or the upload fails — the caller MUST
 *   NOT persist a ref it did not get back (STG-B-003: never report a write as
 *   stored when it is not, and never leave a block pointing at a blob that
 *   does not exist).
 */
export async function uploadDocumentImage(docId, bytes, contentType) {
  const ext = IMAGE_EXT_BY_TYPE[contentType];
  if (!docId) throw new Error('uploadDocumentImage: missing docId');
  if (!ext) throw new Error(`uploadDocumentImage: unsupported type ${contentType}`);
  const hash = await sha256Hex(bytes);
  const ref = `_images/${encodeURIComponent(docId)}/${hash}.${ext}`;
  await azurePost('/api/documents-upload', {
    filename: ref,
    contentType,
    data: base64FromBytes(new Uint8Array(bytes)),
  });
  return ref;
}

/**
 * Read a document image back as a data: URL.
 *
 * Goes through the Function App rather than a blob SAS URL on purpose — see
 * azure-functions/documents-image/index.js. A data: URL never taints the canvas
 * html2canvas draws into, so PDF export keeps working, and it needs no
 * storage-account CORS.
 *
 * @param {string} ref
 * @returns {Promise<string>} a `data:<type>;base64,<...>` URL
 */
export async function fetchDocumentImage(ref) {
  const { contentType, data } = await azureGet('/api/documents-image', { ref });
  if (!contentType || !data) throw new Error('documents-image: empty response');
  return `data:${contentType};base64,${data}`;
}

// ── Product SBOM upload (Products / PDE module — CRA Art. 13(5)) ───────────────
// Stores a per-product machine-readable SBOM (CycloneDX/SPDX JSON) in the
// 'documents' blob container under { tenantId }/_sboms/<productId>/<file>. The
// blob path is prefixed with the JWT tenantId server-side, so SBOMs are scoped
// per tenant and per product (PROD-B-001, PROD-B-003). Returns the blob ref to
// store as product.sbomRef.
/**
 * @param {string | null | undefined} productId
 * @param {string | null | undefined} filename - defaults to `sbom.json`
 * @param {string | null | undefined} text - the SBOM document body
 * @returns {Promise<string | null>} the blob ref to store as `product.sbomRef`
 */
export async function uploadSbom(productId, filename, text) {
  if (!productId || !text) return null;
  const safeName = String(filename || 'sbom.json').replace(/[^a-zA-Z0-9._-]/g, '_');
  const blobPath = `_sboms/${encodeURIComponent(productId)}/${safeName}`;
  const base64 =
    typeof btoa === 'function'
      ? btoa(unescape(encodeURIComponent(text)))
      : Buffer.from(text, 'utf8').toString('base64');
  await azurePost('/api/documents-upload', {
    filename: blobPath,
    contentType: 'application/json',
    data: base64,
  });
  return blobPath;
}

// ── Artefact evidence files ─ blob-backed, tenant-scoped ───────────────
// An artefact in the evidence register may carry an uploaded file. The bytes
// live in the same tenant-scoped 'documents' blob container as every other
// blob this module writes, under `_artefacts/<artefactId>/<filename>`, and the
// artefact record stores only that ref as `storagePath`.
//
// These replace two stubs in src/api/evidenceStorage.js that returned null:
// an "upload" discarded the bytes in the browser while keeping the file's
// NAME, size and mime type on the record, and nothing anywhere could read a
// file back. That is not a cosmetic gap — evaluateControl()'s gate 4 counts a
// blob-backed artefact as evidence on the strength of storagePath/filename
// (ARTF-B-002), so a register of file-less artefacts reported evidence that
// was never stored, which is exactly the claim artefacts.json's
// `mustNeverClaim` forbids.
//
// They live here, in the one storage module (STG-B-001), rather than in a
// second module that turns a storage operation into an Azure Function call.
// No new Azure Function: documents-upload and documents-download already do
// tenant-prefixed blob writes and time-limited SAS reads, with the extension
// allowlist, MIME allowlist, magic-byte sniff, 20 MB cap and
// Content-Disposition: attachment that evidence files need.

/**
 * Reduce a user-supplied filename to what documents-upload will accept as a
 * blob path segment, keeping the extension intact — the endpoint rejects an
 * extensionless name outright, and derives its allowlist check from it.
 *
 * @param {string | null | undefined} name
 * @returns {string}
 */
function safeBlobFilename(name) {
  return String(name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');
}

/**
 * Store an artefact's file and return the ref to persist as
 * `artefact.storagePath`.
 *
 * Throws when the upload is refused (disallowed type, oversized, no upload
 * permission) or fails. The caller MUST let that reach the user rather than
 * saving an artefact that names a file nobody stored (STG-B-003).
 *
 * @param {string} artefactId
 * @param {File | Blob & { name?: string, type?: string }} file
 * @returns {Promise<string>} `_artefacts/<artefactId>/<filename>`
 */
export async function uploadArtefactFile(artefactId, file) {
  if (!artefactId) throw new Error('uploadArtefactFile: missing artefactId');
  if (!file) throw new Error('uploadArtefactFile: missing file');
  const filename = safeBlobFilename(/** @type {any} */ (file).name);
  const storagePath = `_artefacts/${encodeURIComponent(artefactId)}/${filename}`;
  const bytes = new Uint8Array(await file.arrayBuffer());
  await azurePost('/api/documents-upload', {
    filename: storagePath,
    contentType: file.type || 'application/octet-stream',
    data: base64FromBytes(bytes),
  });
  return storagePath;
}

/**
 * Store a candidate's CV and return the ref to persist as
 * `candidate.cvDocumentId`.
 *
 * Namespaced per candidate like every other caller of documents-upload:
 * written at the tenant root, two candidates' "CV.pdf" were one blob, and a
 * CV could replace any other tenant file of the same name.
 *
 * Throws when the upload is refused or fails; the caller must not save a ref
 * it did not get back (STG-B-003).
 *
 * @param {string} candidateId
 * @param {File | Blob & { name?: string, type?: string }} file
 * @returns {Promise<string>} `_candidates/<candidateId>/cv/<filename>`
 */
export async function uploadCandidateCv(candidateId, file) {
  if (!candidateId) throw new Error('uploadCandidateCv: missing candidateId');
  if (!file) throw new Error('uploadCandidateCv: missing file');
  const filename = safeBlobFilename(/** @type {any} */ (file).name);
  const storagePath = `_candidates/${encodeURIComponent(candidateId)}/cv/${filename}`;
  const bytes = new Uint8Array(await file.arrayBuffer());
  await azurePost('/api/documents-upload', {
    filename: storagePath,
    contentType: file.type || 'application/octet-stream',
    data: base64FromBytes(bytes),
  });
  return storagePath;
}

/**
 * Mint a time-limited download URL for a stored evidence file.
 *
 * Resolves to null when there is nothing to read — no ref, or a ref whose
 * blob is gone — so a caller can render "no file" rather than a dead link.
 *
 * @param {string | null | undefined} storagePath
 * @returns {Promise<string | null>}
 */
export async function getEvidenceFileUrl(storagePath) {
  if (!storagePath) return null;
  try {
    const { url } = await azureGet('/api/documents-download', { filename: storagePath });
    return url || null;
  } catch (err) {
    console.error('[storage-azure] evidence file URL failed:', err);
    return null;
  }
}

// ── Tenant-scoped users ───────────────────────────────────────────────────────

/**
 * Read the tenant's user rows. Never throws — logs and resolves to `[]`.
 *
 * @param {string} tenantId - only sent when `env` is set; otherwise the tenant
 *   is resolved from the JWT server-side
 * @param {EnvOptions} [opts]
 * @returns {Promise<ApiRecord[]>}
 */
export async function readTenantUsers(tenantId, opts = {}) {
  return safeRead(
    'readTenantUsers',
    async () => (await apiGet('/api/users-crud', tenantScope(tenantId, opts), opts)).users ?? [],
    []
  );
}

/**
 * Upsert user records. **Upsert-only** — a user missing from `users` is left
 * alone, never deleted (use `deleteTenantUser`). Each record carries the
 * `_userVersion` it was read with, so a concurrent edit to the SAME user comes
 * back as a 409 instead of silently overwriting the other admin's change.
 *
 * @param {string} tenantId - only sent when `env` is set; otherwise the tenant
 *   comes from the JWT
 * @param {ApiRecord[]} users
 * @param {EnvOptions & { replace?: boolean }} [opts] - `replace: true` is
 *   super-admin only and removes users absent from `users` (demo-data reset)
 * @returns {Promise<{ ok?: boolean, versions?: Record<string,string> }>}
 */
export async function writeTenantUsers(tenantId, users, opts = {}) {
  try {
    const body = {
      ...tenantScope(tenantId, opts),
      users,
      ...(opts.replace ? { mode: 'replace' } : {}),
    };
    return await apiPost('/api/users-crud', body, opts);
  } catch (e) {
    console.error('[storage-azure] writeTenantUsers failed:', /** @type {any} */ (e).message);
    throw e;
  }
}

/**
 * Delete ONE user. Deletion is an explicit action rather than an omission from
 * the saved array — an omitted user is far more often one this browser never
 * fetched than one the admin meant to remove.
 *
 * @param {string} userId
 * @param {EnvOptions} [opts] - pass `{ env, tenantId }` for a cross-env delete
 * @returns {Promise<void>}
 */
export async function deleteTenantUser(userId, opts = {}) {
  await apiPost(
    '/api/users-crud',
    { action: 'delete', ...tenantScope(opts.tenantId, opts), userId },
    opts
  );
}

/**
 * Sets a user's login password directly (admin action). Unlike writeTenantUsers,
 * this never round-trips through the general array save — that path always keeps
 * the existing passwordHash for existing users, so it can never actually change
 * a password. This is the only writable path for changing an existing user's
 * password from the app; it never touches MFA state.
 * @param {string} userId
 * @param {string} newPassword
 * @param {EnvOptions} [opts] - pass `{ env, tenantId }` for a cross-env
 *   super-admin reset
 * @returns {Promise<void>}
 */
export async function setUserPassword(userId, newPassword, opts = {}) {
  await apiPost(
    '/api/users-crud',
    { action: 'set-password', ...tenantScope(opts.tenantId, opts), userId, newPassword },
    opts
  );
}

/**
 * Clear a user's MFA enrolment and issue a temporary password. Recovery action
 * for a user who has lost their second factor.
 *
 * The response shape depends on the caller's role, because the delivery path
 * does:
 *   - tenant admin → `{ ok, emailed: true, recipientEmail, expiresAt }`. The
 *     password is emailed to the target user and deliberately never returned,
 *     so a hijacked admin session cannot read it (see the §16 note in
 *     azure-functions/users-crud/index.js).
 *   - super-admin  → `{ ok, tempPassword, expiresAt }`, no email sent.
 *
 * @param {string} userId
 * @param {EnvOptions} [opts] - pass `{ env, tenantId }` for a cross-env reset
 * @returns {Promise<ApiRecord>}
 */
export async function resetUserMfa(userId, opts = {}) {
  return apiPost(
    '/api/users-crud',
    { action: 'reset-mfa', ...tenantScope(opts.tenantId, opts), userId },
    opts
  );
}

// ── Tenant registry (super-admin) ─────────────────────────────────────────────

/**
 * Read the registry rows for an environment.
 *
 * Two endpoints, deliberately: `registry-read` is the unauthenticated read
 * used for tenant resolution and trims the rows down (ISOL-B-002), while
 * `registry-read-admin` returns the untrimmed rows (plan, adminEmail,
 * branding, diagnostics, …) behind a super-admin JWT. The super-admin portal
 * must use the untrimmed read — reading trimmed rows silently dropped fields
 * and corrupted the registry on read-modify-write edits (e.g. plan reverting).
 *
 * On the unauthenticated path the registry no longer hands back every tenant
 * (ISOL-B-003) — name the one you want with `slug` or `customDomain`. `all`
 * asks for the full list and is honoured only on dev/test registries; the
 * production registry answers it with an empty array. Passing no lookup option
 * lets the endpoint infer the tenant from the request's origin host, which is
 * what a client that has not yet resolved its own tenant relies on.
 *
 * @param {EnvOptions & { slug?: string, customDomain?: string, all?: boolean }} [opts]
 * @returns {Promise<ApiRecord[]>} the matching tenant rows in the target registry
 */
export async function readTenantRegistry(opts = {}) {
  const path = opts.env ? '/api/registry-read-admin' : '/api/registry-read';
  /** @type {{ registryId: string, slug?: string, customDomain?: string, all?: string }} */
  const params = { registryId: registryIdFor(opts.env) };

  // The super-admin read is already gated and returns the untrimmed list whole.
  if (!opts.env) {
    if (opts.slug) params.slug = opts.slug;
    else if (opts.customDomain) params.customDomain = opts.customDomain;
    else if (opts.all) params.all = '1';
  }

  const data = await apiGet(path, params, opts);
  return data.tenants ?? [];
}

/**
 * @param {ApiRecord[]} tenants
 * @param {EnvOptions} [opts]
 * @returns {Promise<void>}
 */
export async function writeTenantRegistry(tenants, opts = {}) {
  await apiPost('/api/registry-write', { tenants, registryId: registryIdFor(opts.env) }, opts);
}

/**
 * Atomically provision a tenant — registry row, initial state and the default
 * administrator user are created server-side in one call.
 *
 * @param {{ name: string, slug: string, adminEmail: string, plan?: string }} payload
 * @param {EnvOptions} [opts]
 * @returns {Promise<{ tenant: ApiRecord, temporaryPassword: string }>}
 */
export async function provisionTenant({ name, slug, adminEmail, plan, nonProduction }, opts = {}) {
  return apiPost(
    '/api/tenant-provision',
    {
      name,
      slug,
      adminEmail,
      plan,
      registryId: registryIdFor(opts.env),
      // GH #6444 — the caller's explicit declaration that this tenant holds no
      // real customer data. Required by the dev/test registries, refused by the
      // production one. Deliberately passed through rather than derived from
      // `opts.env`: deriving it would make the declaration a restatement of the
      // dropdown the operator just used, which is the "safe by habit" state
      // ISOL-B-003's carve-out was already in.
      nonProduction: nonProduction === true,
    },
    opts
  );
}

// ── Super-admin users ─────────────────────────────────────────────────────────

/**
 * The super-admin roster and the version it was read at (FDNA-02b). Throws on
 * failure — a roster screen must not mistake an unreadable roster for an
 * empty one.
 * @returns {Promise<{ users: ApiRecord[], version: string | null }>}
 */
export async function readSuperAdminUsers() {
  const res = await azureGet('/api/superadmin-users');
  return { users: res.users ?? [], version: res.version ?? null };
}

/**
 * Replace the super-admin roster. Throws on failure — a refused save (400,
 * 409 when the roster changed since `version` was read, or a cancelled
 * step-up) must never look like a saved one.
 * @param {ApiRecord[]} users
 * @param {string | null} version - the `version` readSuperAdminUsers returned
 * @returns {Promise<void>}
 */
export async function writeSuperAdminUsers(users, version) {
  await azurePost('/api/superadmin-users', { users, version });
}

// ── Access requests ───────────────────────────────────────────────────────────

/**
 * Never throws — logs and resolves to `[]`.
 * @param {string} tenantId - only sent when `env` is set; otherwise the tenant
 *   is resolved from the JWT server-side
 * @param {EnvOptions} [opts]
 * @returns {Promise<ApiRecord[]>}
 */
export async function readAccessRequests(tenantId, opts = {}) {
  return safeRead(
    'readAccessRequests',
    async () =>
      (await apiGet('/api/access-requests', tenantScope(tenantId, opts), opts)).requests ?? [],
    []
  );
}

/**
 * @param {string} tenantId - only sent when `env` is set; otherwise the tenant
 *   comes from the JWT
 * @param {ApiRecord[]} requests
 * @param {EnvOptions} [opts]
 * @returns {Promise<void>}
 */
export async function writeAccessRequests(tenantId, requests, opts = {}) {
  try {
    await apiPost('/api/access-requests', { ...tenantScope(tenantId, opts), requests }, opts);
  } catch (e) {
    console.error('[storage-azure] writeAccessRequests failed:', /** @type {any} */ (e).message);
    throw e;
  }
}

// ── Delete all tenant data ────────────────────────────────────────────────────

/**
 * Erase a tenant's data. Propagates failures — a swallowed error here would
 * report a successful deletion while the tenant's rows are still in the
 * database, so the caller must be able to surface it.
 *
 * @param {string} tenantId
 * @param {EnvOptions} [opts]
 * @returns {Promise<void>}
 */
export async function deleteTenantData(tenantId, opts = {}) {
  await apiPost('/api/tenant-delete', { tenantId }, opts);
}

// ── Framework catalogue (super-admin, global) ─────────────────────────────────

/**
 * Never throws — logs and resolves to `[]`.
 * @param {EnvOptions} [opts]
 * @returns {Promise<ApiRecord[]>}
 */
export async function readFrameworkCatalogue(opts = {}) {
  return safeRead(
    'readFrameworkCatalogue',
    async () => (await apiGet('/api/framework-catalogue', {}, opts)).frameworks ?? [],
    []
  );
}

/**
 * @param {ApiRecord[]} frameworks
 * @param {EnvOptions} [opts]
 * @returns {Promise<void>}
 */
export async function writeFrameworkCatalogue(frameworks, opts = {}) {
  try {
    await apiPost('/api/framework-catalogue', { frameworks }, opts);
  } catch (e) {
    console.error(
      '[storage-azure] writeFrameworkCatalogue failed:',
      /** @type {any} */ (e).message
    );
    throw e;
  }
}

// ── App version ──────────────────────────────────────────────────────────────

/**
 * Never throws — logs and resolves to `null`.
 * @param {EnvOptions} [opts]
 * @returns {Promise<string | null>} the environment's `app_version` registry value
 */
export async function readAppVersion(opts = {}) {
  return safeRead(
    'readAppVersion',
    async () =>
      (await apiGet('/api/registry-read', { registryId: registryIdFor(opts.env) }, opts))
        .app_version ?? null,
    null
  );
}

/**
 * @param {string} version
 * @param {EnvOptions} [opts]
 * @returns {Promise<void>}
 */
export async function writeAppVersion(version, opts = {}) {
  await apiPost(
    '/api/registry-write',
    { app_version: version, registryId: registryIdFor(opts.env) },
    opts
  );
}

// ── UI layout (global, per-environment) ──────────────────────────────────────

/**
 * Never throws — logs and resolves to `null`.
 * @param {EnvOptions} [opts]
 * @returns {Promise<string | null>} the environment's `ui_layout` registry value
 */
export async function readUiLayout(opts = {}) {
  return safeRead(
    'readUiLayout',
    async () =>
      (await apiGet('/api/registry-read', { registryId: registryIdFor(opts.env) }, opts))
        .ui_layout ?? null,
    null
  );
}

/**
 * @param {string} layoutId
 * @param {EnvOptions} [opts]
 * @returns {Promise<void>}
 */
export async function writeUiLayout(layoutId, opts = {}) {
  await apiPost(
    '/api/registry-write',
    { ui_layout: layoutId, registryId: registryIdFor(opts.env) },
    opts
  );
}

// ── Bulk mailer: saved additional recipients (global, prod row only) ────────
//
// Deliberately not routed through `opts.env` / registryIdFor: this list is
// real people, not per-environment config, so it always lives on the single
// canonical 'registry' (production) row regardless of which environment the
// super-admin portal happens to be viewing. Reads go through the admin-only
// registry-read-admin endpoint (bulk_mail_recipients is not on the public
// registry-read response).

/**
 * Never throws — logs and resolves to `[]`.
 * @returns {Promise<Array<{ email: string, label: string }>>}
 */
export async function readBulkMailRecipients() {
  return safeRead(
    'readBulkMailRecipients',
    async () =>
      (await apiGet('/api/registry-read-admin', { registryId: 'registry' })).bulk_mail_recipients ??
      [],
    []
  );
}

/**
 * @param {Array<{ email: string, label?: string }>} recipients
 * @returns {Promise<void>}
 */
export async function writeBulkMailRecipients(recipients) {
  await apiPost('/api/registry-write', {
    bulk_mail_recipients: recipients,
    registryId: 'registry',
  });
}

/**
 * Send the super-admin Bulk Mailer announcement.
 * @param {{ subject: string, sections: Array<{heading?:string, body?:string}>, recipients: Array<{email:string}> }} payload
 * @returns {Promise<{ broadcastId: string, total: number, sent: number, failed: Array }>}
 */
export async function sendBulkMail(payload) {
  return apiPost('/api/super-admin-bulk-mail-send', payload);
}

// ── Supplier: Customers (end-customers of deliveries) ────────────────────────

/**
 * Never throws — logs and resolves to `[]`.
 * @returns {Promise<ApiRecord[]>}
 */
export async function listCustomers() {
  return safeRead(
    'listCustomers',
    async () =>
      (await azurePost('/api/supplier-customers-crud', { action: 'list' })).customers ?? [],
    []
  );
}

/**
 * Never throws — logs and resolves to `null`.
 * @param {string} customerId
 * @returns {Promise<ApiRecord | null>}
 */
export async function getCustomer(customerId) {
  return safeRead(
    'getCustomer',
    async () =>
      (await azurePost('/api/supplier-customers-crud', { action: 'get', customerId })).customer ??
      null,
    null
  );
}

/**
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the created customer
 */
export async function createCustomer(payload) {
  const data = await azurePost('/api/supplier-customers-crud', {
    action: 'create',
    payload,
  });
  return data.customer;
}

/**
 * @param {string} customerId
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the updated customer
 */
export async function updateCustomer(customerId, payload) {
  const data = await azurePost('/api/supplier-customers-crud', {
    action: 'update',
    customerId,
    payload,
  });
  return data.customer;
}

/**
 * @param {string} customerId
 * @returns {Promise<ApiRecord>} the deleted customer
 */
export async function deleteCustomer(customerId) {
  const data = await azurePost('/api/supplier-customers-crud', {
    action: 'delete',
    customerId,
  });
  return data.customer;
}

// ── Delivery Agreements ──────────────────────────────────────────────────

/**
 * Never throws — logs and resolves to `[]`.
 * @returns {Promise<ApiRecord[]>}
 */
export async function listDeliveryAgreements() {
  return safeRead(
    'listDeliveryAgreements',
    async () =>
      (
        await azurePost('/api/supplier-delivery-agreements-crud', {
          entity: 'agreement',
          action: 'list',
        })
      ).agreements ?? [],
    []
  );
}

/**
 * Never throws — logs and resolves to `null`.
 * @param {string} agreementId
 * @returns {Promise<ApiRecord | null>}
 */
export async function getDeliveryAgreement(agreementId) {
  return safeRead(
    'getDeliveryAgreement',
    async () =>
      (
        await azurePost('/api/supplier-delivery-agreements-crud', {
          entity: 'agreement',
          action: 'get',
          agreementId,
        })
      ).agreement ?? null,
    null
  );
}

/**
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the created delivery agreement
 */
export async function createDeliveryAgreement(payload) {
  const data = await azurePost('/api/supplier-delivery-agreements-crud', {
    entity: 'agreement',
    action: 'create',
    payload,
  });
  return data.agreement;
}

/**
 * @param {string} agreementId
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the updated delivery agreement
 */
export async function updateDeliveryAgreement(agreementId, payload) {
  const data = await azurePost('/api/supplier-delivery-agreements-crud', {
    entity: 'agreement',
    action: 'update',
    agreementId,
    payload,
  });
  return data.agreement;
}

/**
 * @param {string} agreementId
 * @returns {Promise<ApiRecord>} the closed delivery agreement
 */
export async function closeDeliveryAgreement(agreementId) {
  const data = await azurePost('/api/supplier-delivery-agreements-crud', {
    entity: 'agreement',
    action: 'close',
    agreementId,
  });
  return data.agreement;
}

// ── Engagements ──────────────────────────────────────────────────────────

/**
 * Never throws — logs and resolves to `[]`.
 * @param {string} [agreementId] - restricts the list to one delivery agreement
 * @returns {Promise<ApiRecord[]>}
 */
export async function listEngagements(agreementId) {
  return safeRead(
    'listEngagements',
    async () =>
      (
        await azurePost('/api/supplier-delivery-agreements-crud', {
          entity: 'engagement',
          action: 'list',
          agreementId,
        })
      ).engagements ?? [],
    []
  );
}

/**
 * Never throws — logs and resolves to `null`.
 * @param {string} engagementId
 * @returns {Promise<ApiRecord | null>}
 */
export async function getEngagement(engagementId) {
  return safeRead(
    'getEngagement',
    async () =>
      (
        await azurePost('/api/supplier-delivery-agreements-crud', {
          entity: 'engagement',
          action: 'get',
          engagementId,
        })
      ).engagement ?? null,
    null
  );
}

/**
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the created engagement
 */
export async function createEngagement(payload) {
  const data = await azurePost('/api/supplier-delivery-agreements-crud', {
    entity: 'engagement',
    action: 'create',
    payload,
  });
  return data.engagement;
}

/**
 * @param {string} engagementId
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the updated engagement
 */
export async function updateEngagement(engagementId, payload) {
  const data = await azurePost('/api/supplier-delivery-agreements-crud', {
    entity: 'engagement',
    action: 'update',
    engagementId,
    payload,
  });
  return data.engagement;
}

/**
 * @param {string} engagementId
 * @returns {Promise<ApiRecord>} the closed engagement
 */
export async function closeEngagement(engagementId) {
  const data = await azurePost('/api/supplier-delivery-agreements-crud', {
    entity: 'engagement',
    action: 'close',
    engagementId,
  });
  return data.engagement;
}

// ── Subcontractor Involvements ───────────────────────────────────────────────

/**
 * Never throws — logs and resolves to `[]`.
 * @param {ApiRecord} [filters] - merged into the request body as extra filters
 * @returns {Promise<ApiRecord[]>}
 */
export async function listInvolvements(filters = {}) {
  return safeRead(
    'listInvolvements',
    async () =>
      (await azurePost('/api/supplier-involvements-crud', { action: 'list', ...filters }))
        .involvements ?? [],
    []
  );
}

/**
 * Never throws — logs and resolves to `null`.
 * @param {string} involvementId
 * @returns {Promise<ApiRecord | null>}
 */
export async function getInvolvement(involvementId) {
  return safeRead(
    'getInvolvement',
    async () =>
      (await azurePost('/api/supplier-involvements-crud', { action: 'get', involvementId }))
        .involvement ?? null,
    null
  );
}

/**
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the created involvement
 */
export async function createInvolvement(payload) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'create',
    payload,
  });
  return data.involvement;
}

/**
 * @param {string} involvementId
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the updated involvement
 */
export async function updateInvolvement(involvementId, payload) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'update',
    involvementId,
    payload,
  });
  return data.involvement;
}

/**
 * @param {string} involvementId
 * @returns {Promise<ApiRecord>} the offboarded involvement
 */
export async function offboardInvolvement(involvementId) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'offboard',
    involvementId,
  });
  return data.involvement;
}

// ── Requirement Matrix (SKI 02.15, SKIA-03) ──────────────────────────────
//
// Rows are never overlaid into tenant_state — fetched only via these two
// dedicated functions, same posture as documents. Derivation itself is
// computed client-side (src/utils/requirementMatrix.js) and synced here;
// these are thin HTTP wrappers only, per STG-B-001.

/**
 * Never throws — logs and resolves to `[]`.
 * @param {string} agreementId
 * @returns {Promise<ApiRecord[]>} persisted agreement-level matrix rows
 */
export async function getAgreementRequirementMatrix(agreementId) {
  return safeRead(
    'getAgreementRequirementMatrix',
    async () =>
      (await azurePost('/api/requirement-matrix-read', { scope: 'agreement', agreementId })).rows ??
      [],
    []
  );
}

/**
 * Never throws — logs and resolves to `[]`.
 * @param {string} involvementId
 * @returns {Promise<ApiRecord[]>} persisted involvement-level matrix rows
 */
export async function getInvolvementRequirementMatrix(involvementId) {
  return safeRead(
    'getInvolvementRequirementMatrix',
    async () =>
      (await azurePost('/api/requirement-matrix-read', { scope: 'involvement', involvementId }))
        .rows ?? [],
    []
  );
}

/**
 * Upsert a freshly-derived desired row set. Each row is independently
 * version-checked; a conflict on one row never blocks the others.
 * @param {{ scope: 'agreement'|'involvement', agreementId?: string, involvementId?: string, rows: Array<{externalId: string, version: string|null, data: object}> }} params
 * @returns {Promise<Array<object>>} per-row { externalId, status, version? }
 */
export async function syncRequirementMatrix({ scope, agreementId, involvementId, rows }) {
  const data = await azurePost('/api/requirement-matrix-write', {
    action: 'sync',
    scope,
    agreementId,
    involvementId,
    rows,
  });
  return data.results ?? [];
}

/**
 * Batch-patch the `review` field of N involvement rows in one call — the
 * write shape SKIC-01's bulk AI-verdict-confirm is designed against.
 * @param {string} involvementId
 * @param {Array<{externalId: string, version: string, review: object}>} patches
 * @returns {Promise<Array<object>>} per-row { externalId, status, version? }
 */
export async function patchInvolvementRequirementReviews(involvementId, patches) {
  const data = await azurePost('/api/requirement-matrix-write', {
    action: 'review-patch',
    involvementId,
    patches,
  });
  return data.results ?? [];
}

/**
 * Bulk-affirm carried-forward verdicts UNCHANGED for a new revalidation
 * cycle (SKIC-03). Only ever eligible for rows the server classified as
 * non-delta (`review.carriedForward.reasons.length === 0`) — the endpoint
 * refuses (422) any other row, so this can never be used to wave through
 * a requirement flagged for individual re-review. A delta row must instead
 * be re-reviewed through patchInvolvementRequirementReviews.
 *
 * @param {string} involvementId
 * @param {string[]} externalIds
 * @returns {Promise<Array<object>>} per-row { externalId, status, version? }
 */
export async function affirmCarryForwardVerdicts(involvementId, externalIds) {
  const data = await azurePost('/api/requirement-matrix-write', {
    action: 'affirm-carry-forward',
    involvementId,
    externalIds,
  });
  return data.results ?? [];
}

/**
 * Ask the AI layer to PROPOSE a verdict per applicable requirement for one
 * involvement (SKIC-01). Proposals are suggestions only — they are persisted
 * to each row's `proposal` field and are never a verdict; confirming one is a
 * separate, explicit human action through patchInvolvementRequirementReviews
 * (SUB-B-008, RM-B-007).
 *
 * @param {string} involvementId
 * @param {string[]} [externalIds] - optional subset (re-propose exceptions
 *   only); always intersected server-side with the involvement's own rows.
 * @returns {Promise<{ proposals: Array<object>, skipped: number, generatedAt: string|null, advisory: string }>}
 */
// ── SKID-01 — subcontractor consultant / device register ────────────────────
//
// Thin HTTP wrappers only (STG-B-001). The register is personal data held in
// its own per-record table and is deliberately NOT part of tenant state, so
// it is fetched on demand by the involvement that needs it — never loaded
// with the rest of the app.

/**
 * Read one involvement's consultant / device register for review.
 * Never throws — logs and resolves to an empty register.
 *
 * @param {string} involvementId
 * @returns {Promise<{ consultants: Array<object>, duplicateSerials: Array<object>, counts: object, personalDataNotice: boolean }>}
 */
export async function getInvolvementPersonnel(involvementId) {
  return safeRead(
    'getInvolvementPersonnel',
    async () => {
      const data = await azurePost('/api/supplier-personnel-read', { involvementId });
      return {
        consultants: data.consultants ?? [],
        duplicateSerials: data.duplicateSerials ?? [],
        counts: data.counts ?? {},
        personalDataNotice: Boolean(data.personalDataNotice),
      };
    },
    { consultants: [], duplicateSerials: [], counts: {}, personalDataNotice: false }
  );
}

/**
 * Record a VENZO review decision on a consultant or device, or move one
 * through its lifecycle. This is the review surface — there is deliberately
 * no create/edit of a consultant or device here: capture belongs to the
 * subcontractor's portal (SKID-01).
 *
 * Throws on failure so the caller can surface it — unlike the read above, a
 * silently-swallowed verification would leave the reviewer believing they
 * recorded a decision they did not.
 *
 * @param {{ action: 'verify-consultant'|'verify-device'|'offboard-consultant'|'retire-device',
 *           involvementId: string, consultantId: string, deviceId?: string,
 *           status?: 'verified'|'rejected', note?: string, reason?: string }} params
 * @returns {Promise<object>} { success, consultant? , device? }
 */
export async function writeInvolvementPersonnel(params) {
  return azurePost('/api/supplier-personnel-write', params);
}

// ── SKID-02 — per-device BYOD control points ─────────────────────────────
//
// Reuses the requirement-matrix read/write endpoints (scope:'device' /
// action:'device-review-patch') and the SKIB-01 five-status review shape
// verbatim — see requirement-matrix-read/write's headers.

/**
 * SKIA-06 — the two package B (Basis) signature obligation rows for one
 * consultant: the customer's NDA and the customer's AUP. Rows are derived
 * server-side on read (ensure-on-read), exactly like device control points.
 * @param {string} involvementId
 * @param {string} consultantId
 * @returns {Promise<ApiRecord[]>}
 */
export async function getPackageBObligations(involvementId, consultantId) {
  return safeRead(
    'getPackageBObligations',
    async () =>
      (
        await azurePost('/api/requirement-matrix-read', {
          scope: 'consultant',
          involvementId,
          consultantId,
        })
      ).rows ?? [],
    []
  );
}

/**
 * SKIA-06 — batch-patch the `review` field of N package B obligation rows for
 * one consultant. Same batch shape and independently-version-checked contract
 * as patchDeviceControlPointReviews.
 * @param {string} involvementId
 * @param {string} consultantId
 * @param {Array<{obligationId: string, version: string, review: object}>} patches
 * @returns {Promise<Array<object>>} per-row { obligationId, status, version? }
 */
export async function patchPackageBObligationReviews(involvementId, consultantId, patches) {
  const data = await azurePost('/api/requirement-matrix-write', {
    action: 'consultant-obligation-review-patch',
    involvementId,
    consultantId,
    patches,
  });
  return data.results ?? [];
}

/**
 * Read (and ensure-derive) the 8 BYOD control-point rows for one device.
 * Never throws — logs and resolves to `[]`.
 * @param {string} involvementId
 * @param {string} consultantId
 * @param {string} deviceId
 * @returns {Promise<ApiRecord[]>}
 */
export async function getDeviceControlPoints(involvementId, consultantId, deviceId) {
  return safeRead(
    'getDeviceControlPoints',
    async () =>
      (
        await azurePost('/api/requirement-matrix-read', {
          scope: 'device',
          involvementId,
          consultantId,
          deviceId,
        })
      ).rows ?? [],
    []
  );
}

/**
 * Batch-patch the `review` field of N device control-point rows in one call
 * — the same batch shape as patchInvolvementRequirementReviews, reused for
 * SKIC-01's bulk-confirm pattern at device scope.
 * @param {string} involvementId
 * @param {string} consultantId
 * @param {string} deviceId
 * @param {Array<{pointId: string, version: string, review: object}>} patches
 * @returns {Promise<Array<object>>} per-row { pointId, status, version? }
 */
export async function patchDeviceControlPointReviews(
  involvementId,
  consultantId,
  deviceId,
  patches
) {
  const data = await azurePost('/api/requirement-matrix-write', {
    action: 'device-review-patch',
    involvementId,
    consultantId,
    deviceId,
    patches,
  });
  return data.results ?? [];
}

/**
 * @param {string} involvementId
 * @param {string[]} [externalIds]
 * @returns {Promise<{proposals: ApiRecord[], skipped: number, generatedAt: string|null, advisory: string}>}
 */
export async function proposeRequirementVerdicts(involvementId, externalIds) {
  const data = await azurePost('/api/requirement-verdict-propose', {
    involvementId,
    ...(Array.isArray(externalIds) && externalIds.length > 0 ? { externalIds } : {}),
  });
  return {
    proposals: data.proposals ?? [],
    skipped: data.skipped ?? 0,
    generatedAt: data.generatedAt ?? null,
    advisory: data.advisory ?? '',
  };
}

// ── Evidence reuse (SKIC-02) ─────────────────────────────────────────────
//
// Reuse is always PROPOSED, never automatic (SUB-B-008, SUB-B-014). Confirming
// a reuse links the evidence and leaves a verdict proposal; recording the
// verdict remains a separate act through patchInvolvementRequirementReviews.

/**
 * Scan for evidence-derived verdict proposals and cross-agreement reuse
 * candidates for one involvement. Writes proposals and reuse records onto the
 * involvement's matrix rows; never writes a verdict.
 *
 * @param {string} involvementId
 * @returns {Promise<{ directProposals: Array<object>, reuseCandidates: Array<object>, generatedAt: string|null, truncatedSources: number, advisory: string }>}
 */
export async function proposeEvidenceReuse(involvementId) {
  const data = await azurePost('/api/requirement-evidence-reuse', {
    action: 'propose',
    involvementId,
  });
  return {
    directProposals: data.directProposals ?? [],
    reuseCandidates: data.reuseCandidates ?? [],
    generatedAt: data.generatedAt ?? null,
    truncatedSources: data.truncatedSources ?? 0,
    advisory: data.advisory ?? '',
  };
}

/**
 * Confirm reuse candidates. Each confirmation is attributed to the caller's
 * JWT server-side and re-checked against live state, so a candidate whose
 * evidence expired since it was proposed is refused rather than applied.
 *
 * @param {string} involvementId
 * @param {Array<{ externalId: string, reuseId: string }>} confirmations
 * @returns {Promise<Array<object>>} per-entry { externalId, reuseId, status, reason? }
 */
export async function confirmEvidenceReuse(involvementId, confirmations) {
  const data = await azurePost('/api/requirement-evidence-reuse', {
    action: 'confirm',
    involvementId,
    confirmations,
  });
  return data.results ?? [];
}

/**
 * Dismiss reuse candidates. The record is retained with the dismissing user
 * and reason — a declined reuse is an audit fact, not a deletion.
 *
 * @param {string} involvementId
 * @param {Array<{ externalId: string, reuseId: string, reason?: string }>} dismissals
 * @returns {Promise<Array<object>>} per-entry { externalId, reuseId, status }
 */
export async function dismissEvidenceReuse(involvementId, dismissals) {
  const data = await azurePost('/api/requirement-evidence-reuse', {
    action: 'dismiss',
    involvementId,
    dismissals,
  });
  return data.results ?? [];
}

/**
 * Never throws — logs and resolves to `null`.
 * @returns {Promise<ApiRecord | null>} the tenant's supplier risk configuration
 */
export async function getSupplierRiskConfig() {
  return safeRead(
    'getSupplierRiskConfig',
    async () =>
      (await azurePost('/api/supplier-involvements-crud', { action: 'get-config' })).config ?? null,
    null
  );
}

/**
 * @param {ApiRecord} config
 * @returns {Promise<ApiRecord>} the saved supplier risk configuration
 */
export async function saveSupplierRiskConfig(config) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'save-config',
    payload: config,
  });
  return data.config;
}

/**
 * @param {string} involvementId
 * @param {ApiRecord} updates - the contract-checklist fields to change
 * @returns {Promise<ApiRecord>} the updated involvement
 */
export async function updateContractChecklist(involvementId, updates) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'update-checklist',
    involvementId,
    payload: updates,
  });
  return data.involvement;
}

/**
 * @param {ApiRecord} evidence
 * @returns {Promise<ApiRecord>} the created evidence record
 */
export async function addSupplierEvidence(evidence) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'manage-evidence',
    payload: { evidenceAction: 'add', ...evidence },
  });
  return data.evidence;
}

/**
 * @param {string} evidenceId
 * @returns {Promise<void>}
 */
export async function removeSupplierEvidence(evidenceId) {
  await azurePost('/api/supplier-involvements-crud', {
    action: 'manage-evidence',
    payload: { evidenceAction: 'remove', evidenceId },
  });
}

/**
 * @param {ApiRecord} [filters]
 * @returns {Promise<ApiRecord[]>}
 */
export async function listSupplierEvidence(filters = {}) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'manage-evidence',
    payload: { evidenceAction: 'list', ...filters },
  });
  return data.evidence || [];
}

/**
 * @param {string} evidenceId
 * @param {{ issuedAt?: string | null, validUntil?: string | null }} dates - ISO dates
 * @returns {Promise<ApiRecord>} the updated evidence record
 */
export async function updateEvidenceDates(evidenceId, { issuedAt, validUntil }) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'update-evidence-dates',
    evidenceId,
    issuedAt: issuedAt || null,
    validUntil: validUntil || null,
  });
  return data.evidence;
}

/**
 * Ask the supplier AI assistant to read issue/expiry dates out of an evidence
 * document.
 *
 * @param {{
 *   blobName?: string | null,
 *   mimeType?: string | null,
 *   title?: string,
 *   type?: string | null,
 *   textHint?: string | null
 * }} args
 * @returns {Promise<any>} the raw `supplier-ai-assist` response
 */
export async function extractEvidenceDates({ blobName, mimeType, title, type, textHint }) {
  const data = await azurePost('/api/supplier-ai-assist', {
    action: 'extract-dates',
    blobName: blobName || null,
    mimeType: mimeType || null,
    title,
    type: type || null,
    textHint: textHint || null,
  });
  return data;
}

/**
 * Replace an evidence item's linked requirements in one call (SKIB-02).
 * One evidence item may cover many requirements (e.g. an ISO 27001
 * certificate covers dozens), so this always sends the full desired set —
 * never a single add/remove — matching how the review UI presents it as one
 * multi-select interaction.
 *
 * @param {string} evidenceId
 * @param {string[]} requirementRefs - SKI 02.15 requirement externalIds
 * @returns {Promise<ApiRecord>} the updated evidence record
 */
export async function linkEvidenceRequirements(evidenceId, requirementRefs) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'manage-evidence',
    payload: { evidenceAction: 'link-requirements', evidenceId, requirementRefs },
  });
  return data.evidence;
}

/**
 * @param {string} evidenceId
 * @param {ApiRecord} [opts] - merged into the request body (e.g. verifier notes)
 * @returns {Promise<ApiRecord>} the verified evidence record
 */
export async function verifySupplierEvidence(evidenceId, opts = {}) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'verify-evidence',
    payload: { evidenceId, ...opts },
  });
  return data.evidence;
}

/**
 * @param {string} involvementId
 * @param {string} [comment]
 * @returns {Promise<ApiRecord>} the involvement in its submitted-for-approval state
 */
export async function submitInvolvementApproval(involvementId, comment) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'submit-approval',
    involvementId,
    payload: { comment },
  });
  return data.involvement;
}

/**
 * @param {string} involvementId
 * @param {string} decision - the approval outcome
 * @param {ApiRecord} [opts] - merged into the request body (e.g. comment)
 * @returns {Promise<ApiRecord>} the decided involvement
 */
export async function decideInvolvementApproval(involvementId, decision, opts = {}) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'decide-approval',
    involvementId,
    payload: { decision, ...opts },
  });
  return data.involvement;
}

/**
 * SKIE-01 — manually record a stop criterion (chiefly criterion 8, which has
 * no automated signal). Admin/manager only, enforced server-side.
 * @param {string} involvementId
 * @param {number} criterionNumber - 1..8
 * @param {string} detail - documented explanation
 * @returns {Promise<ApiRecord>} the updated involvement
 */
export async function triggerStopCriterion(involvementId, criterionNumber, detail) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'trigger-stop-criterion',
    involvementId,
    payload: { criterionNumber, detail },
  });
  return data.involvement;
}

/**
 * SKIE-01 — the only way an active stop criterion clears. Requires a
 * non-empty documented resolution note; resolvedBy/resolvedAt are stamped
 * server-side from the caller's JWT.
 * @param {string} involvementId
 * @param {string} stopCriterionId
 * @param {string} note
 * @returns {Promise<ApiRecord>} the updated involvement
 */
export async function resolveStopCriterion(involvementId, stopCriterionId, note) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'resolve-stop-criterion',
    involvementId,
    payload: { stopCriterionId, note },
  });
  return data.involvement;
}

/**
 * Never throws — logs and resolves to `null`.
 * @returns {Promise<ApiRecord | null>} the tenant's security-package definitions
 */
export async function getSecurityPackages() {
  return safeRead(
    'getSecurityPackages',
    async () =>
      (await azurePost('/api/supplier-involvements-crud', { action: 'get-security-packages' }))
        .packages ?? null,
    null
  );
}

/**
 * @param {ApiRecord} packages
 * @returns {Promise<ApiRecord>} the saved security-package definitions
 */
export async function saveSecurityPackages(packages) {
  const data = await azurePost('/api/supplier-involvements-crud', {
    action: 'save-security-packages',
    payload: packages,
  });
  return data.packages;
}

/**
 * @param {string} filename
 * @param {string} contentType - MIME type, validated against an allowlist server-side
 * @param {string} data - base64-encoded file contents
 * @returns {Promise<BlobUploadResult>}
 */
export async function uploadSupplierEvidenceFile(filename, contentType, data) {
  const result = await azurePost('/api/supplier-evidence-upload', {
    filename,
    contentType,
    data,
  });
  return result;
}

// ── Agreements Register (Contract Compliance Analysis) ───────────────────────

/**
 * @param {string} filename
 * @param {string} contentType - MIME type, validated against an allowlist server-side
 * @param {string} data - base64-encoded file contents
 * @returns {Promise<BlobUploadResult>}
 */
export async function uploadAgreementFile(filename, contentType, data) {
  const result = await azurePost('/api/agreements-upload', {
    filename,
    contentType,
    data,
  });
  return result;
}

/**
 * Never throws — logs and resolves to `[]`.
 * @returns {Promise<ApiRecord[]>}
 */
export async function listAgreements() {
  try {
    const data = await azurePost('/api/agreements-crud', {
      entity: 'agreement',
      action: 'list',
    });
    return data.agreements ?? [];
  } catch (e) {
    console.error('[storage-azure] listAgreements failed:', /** @type {any} */ (e).message);
    return [];
  }
}

/**
 * Never throws — logs and resolves to `null`.
 * @param {string} agreementId
 * @returns {Promise<ApiRecord | null>}
 */
export async function getAgreement(agreementId) {
  try {
    const data = await azurePost('/api/agreements-crud', {
      entity: 'agreement',
      action: 'get',
      agreementId,
    });
    return data.agreement ?? null;
  } catch (e) {
    console.error('[storage-azure] getAgreement failed:', /** @type {any} */ (e).message);
    return null;
  }
}

/**
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the created agreement
 */
export async function createAgreement(payload) {
  const data = await azurePost('/api/agreements-crud', {
    entity: 'agreement',
    action: 'create',
    payload,
  });
  return data.agreement;
}

/**
 * @param {string} agreementId
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the updated agreement
 */
export async function updateAgreement(agreementId, payload) {
  const data = await azurePost('/api/agreements-crud', {
    entity: 'agreement',
    action: 'update',
    agreementId,
    payload,
  });
  return data.agreement;
}

/**
 * @param {string} agreementId
 * @returns {Promise<ApiRecord>} the deleted agreement
 */
export async function deleteAgreement(agreementId) {
  const data = await azurePost('/api/agreements-crud', {
    entity: 'agreement',
    action: 'delete',
    agreementId,
  });
  return data.agreement;
}

/**
 * Never throws — logs and resolves to `[]`.
 * @param {string} agreementId
 * @returns {Promise<ApiRecord[]>}
 */
export async function listAgreementCommitments(agreementId) {
  try {
    const data = await azurePost('/api/agreements-crud', {
      entity: 'commitment',
      action: 'list',
      agreementId,
    });
    return data.commitments ?? [];
  } catch (e) {
    console.error(
      '[storage-azure] listAgreementCommitments failed:',
      /** @type {any} */ (e).message
    );
    return [];
  }
}

/**
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the created commitment
 */
export async function createAgreementCommitment(payload) {
  const data = await azurePost('/api/agreements-crud', {
    entity: 'commitment',
    action: 'create',
    payload,
  });
  return data.commitment;
}

/**
 * @param {string} commitmentId
 * @param {ApiRecord} payload
 * @returns {Promise<ApiRecord>} the updated commitment
 */
export async function updateAgreementCommitment(commitmentId, payload) {
  const data = await azurePost('/api/agreements-crud', {
    entity: 'commitment',
    action: 'update',
    commitmentId,
    payload,
  });
  return data.commitment;
}

/**
 * @param {string} commitmentId
 * @returns {Promise<void>}
 */
export async function deleteAgreementCommitment(commitmentId) {
  await azurePost('/api/agreements-crud', {
    entity: 'commitment',
    action: 'delete',
    commitmentId,
  });
}

// ── Contract analysis (Contract Compliance Analysis, Phase B1) ───────────────
// SUGGESTION-ONLY (AGR-B-001). analyzeAgreementContract returns candidate
// commitments; nothing is persisted here. recordAgreementAnalysis stamps only
// analysis metadata (analyzedAt/analysisModel) — never the commitments.

/**
 * Run the AI extraction pipeline on client-extracted contract-text chunks.
 * @param {{ agreementId: string, direction?: string, chunks: string[] }} args
 * @returns {Promise<{
 *   agreementId: string,
 *   analysisModel: string | null,
 *   suggestions: ApiRecord[],
 *   meta: { chunksAnalyzed: number, failedChunks: number, truncatedChunks: boolean }
 * }>}
 */
export async function analyzeAgreementContract({ agreementId, direction, chunks }) {
  return azurePost('/api/contract-analyze', { agreementId, direction, chunks });
}

/**
 * Record that analysis ran against an agreement (metadata only, AGR-B-003 safe).
 * @param {string} agreementId
 * @param {string|null} analysisModel
 * @returns {Promise<object>} the updated agreement
 */
export async function recordAgreementAnalysis(agreementId, analysisModel) {
  const data = await azurePost('/api/agreements-crud', {
    entity: 'agreement',
    action: 'record-analysis',
    agreementId,
    payload: { analysisModel },
  });
  return data.agreement;
}

// ── Cited sources (ACEA-03b, COMM-B-003 / COMM-B-013) ───────────────────
// Opens one communications deep link for the signed-in person through the
// same server resolver that composed it. `silent`: a refusal is an expected
// answer ({ ok: false }), and the page shows its own neutral state for it and
// for any failure, so nothing here may reveal whether the source exists.

/**
 * @param {string} link  `/<module>/<recordId>?v=<version>#<anchor>`
 * @returns {Promise<{ ok: false } | { ok: true, link: string, module: string, recordId: string,
 *   version: string, anchor: string, anchorText: string, superseded: boolean,
 *   currentVersion: string|null, currentLink: string|null, approvalBasis: string|null,
 *   title: string, content?: object[], record?: object }>}
 */
export async function readCitedSource(link) {
  return apiGet('/api/comm-source-read', { link }, { silent: true });
}

// ── My personalisation profile (ACEA-02b, COMM-B-005) ────────────────────
// Always the signed-in person's own profile: no call here takes a user id,
// and the endpoint refuses one. The read is `silent` (it runs in the
// background and the view says itself when the store is unavailable, 503);
// writes are not, so an unexpected failure is reported like any other.

/**
 * @returns {Promise<object>} { exists, unreadable, effective, defaults, rowVersion, updatedAt }
 * @throws ApiError with httpStatus 503 when the environment has no profile key
 */
export async function readMyCommProfile() {
  return azurePost('/api/comm-profile', { action: 'read' }, { silent: true });
}

/**
 * @param {{ lens?: string, depth?: string }} choice
 * @param {string|null} rowVersion  the rowVersion last read (null when none)
 * @returns {Promise<object>} the profile view after the write
 * @throws ApiError with httpStatus 409 when the profile changed elsewhere
 */
export async function writeMyCommProfile(choice, rowVersion) {
  return azurePost('/api/comm-profile', { action: 'write', choice, rowVersion });
}

/** @returns {Promise<object>} everything stored about the caller, portable */
export async function exportMyCommProfile() {
  return azurePost('/api/comm-profile', { action: 'export' });
}

/**
 * @param {string|null} rowVersion  the rowVersion last read
 * @returns {Promise<object>} the profile view after the reset (the default)
 */
export async function resetMyCommProfile(rowVersion) {
  return azurePost('/api/comm-profile', { action: 'reset', rowVersion });
}

// ── My priorities — AI "Why it matters" (ACEA-04, COMM-B-002 / COMM-B-011) ─
// Always the signed-in person's own list: the items name records only and the
// server loads each one through the deep-link resolver for this person. Both
// calls are `silent`: the list renders without them, and a failure only means
// no AI lines are shown.

/** @returns {Promise<{ mode: 'off'|'shadow'|'on' }>} the tenant's rollout mode for this person */
export async function readMyPriorityExplainMode() {
  return azurePost('/api/comm-priorities-explain', { action: 'mode' }, { silent: true });
}

/**
 * @param {Array<{ itemId: string, module: string, recordId: string, actionId?: string }>} items
 * @param {string} lens  the person's own wording choice
 * @returns {Promise<{ mode: string, aiGenerated?: true, explanations: object, suggestedFirst: object|null }>}
 */
export async function explainMyPriorities(items, lens) {
  return azurePost(
    '/api/comm-priorities-explain',
    { action: 'explain', items, lens },
    { silent: true }
  );
}

// ── Ask about this and the doubt loop (ACEA-05, COMM-B-011 / -012 / -014) ──
// function-contracts/comm-questions.json. Always the signed-in person's own
// questions and the tasks assigned to them: no call here takes a user id, and
// the server decides who may answer, give feedback or withdraw from the JWT.

/**
 * @param {{ module: string, recordId: string, actionId?: string }} item  the card's record
 * @param {string} question
 * @param {boolean} mandatory  the card is mandatory — the doubt task is due in 1 business day
 * @returns {Promise<{ class: 'answered'|'doubt'|'off-topic'|'unavailable', text?, sentences?, askedRole?, questionId? }>}
 */
export async function askAboutMyPriority(item, question, mandatory) {
  return azurePost('/api/comm-questions', { action: 'ask', item, question, mandatory });
}

/** Put the question to a person instead — no AI. @returns {Promise<{ questionId, askedRole }>} */
export async function askColleagueAboutMyPriority(item, question, mandatory) {
  return azurePost('/api/comm-questions', { action: 'ask-colleague', item, question, mandatory });
}

/**
 * Report one AI sentence as wrong (COMM-B-012).
 * @param {{ item, sentence: string, link: string, note?: string, surface: 'explain'|'ask' }} report
 * @returns {Promise<{ reportId, askedRole }>}
 */
export async function reportWrongAiAnswer({ item, sentence, link, note, surface }) {
  return azurePost('/api/comm-questions', {
    action: 'report',
    item,
    sentence,
    link,
    note,
    surface,
  });
}

/** @returns {Promise<{ questions: object[] }>} the caller's own questions and tasks (silent: background read) */
export async function listMyCommQuestions() {
  return azurePost('/api/comm-questions', { action: 'list' }, { silent: true });
}

/**
 * The assignee closes a task.
 * @param {{ id, rowVersion, outcome: 'answered'|'cannot-answer', text: string, guidance?: { title: string } }} answer
 * @throws ApiError 409 when it changed elsewhere
 */
export async function answerCommQuestion({ id, rowVersion, outcome, text, guidance }) {
  return azurePost('/api/comm-questions', {
    action: 'answer',
    id,
    rowVersion,
    outcome,
    text,
    guidance,
  });
}

/** The asker: did the answer help? A "no" reopens it once. */
export async function giveCommQuestionFeedback({ id, rowVersion, helpful }) {
  return azurePost('/api/comm-questions', { action: 'feedback', id, rowVersion, helpful });
}

/** The asker withdraws (open) or deletes (closed) their own question. */
export async function withdrawCommQuestion({ id, rowVersion }) {
  return azurePost('/api/comm-questions', { action: 'withdraw', id, rowVersion });
}

/**
 * ACEA-06: the doubt-loop report — last complete month, month to date and
 * where each recurring topic stands. ISMS admins and tenant admins only (403
 * otherwise). Tenant counts, never a person (COMM-B-011).
 * @returns {Promise<{ lastMonth, monthToDate, improvements, rules } | { unavailable: true }>}
 */
export async function readDoubtLoopReport() {
  return azurePost('/api/comm-questions', { action: 'loop-report' }, { silent: true });
}

// ── Workflow definitions and runs (PDWA-03) ─────────────────────────────────
//
// docs/workflow-engine-design.md §3a; function-contracts/workflow-definitions-
// write.json, workflow-definitions-read.json, workflow-runs-read.json. A
// published version is never edited: `newWorkflowVersion` starts the next
// draft. Publishing is validated on the server against the tenant's own ids
// and refused with 422 { errors } — the validator's structured errors. Runs
// are written only by the server-side runner; this file only reads them.
// `opts.env` + `tenantId` serve the super-admin console's reads; writes are
// refused for a super-admin JWT, so they take no env.

/**
 * @param {string} [tenantId] only sent when `opts.env` is set
 * @param {EnvOptions} [opts]
 * @returns {Promise<{ definitions: object[] }>} one summary per definition, with its versions
 */
export async function listWorkflowDefinitions(tenantId, opts = {}) {
  return apiGet('/api/workflow-definitions-read', tenantScope(tenantId, opts), opts);
}

/**
 * @param {string} definitionId
 * @param {number} [version] omit for every version of the definition
 * @param {string} [tenantId] only sent when `opts.env` is set
 * @param {EnvOptions} [opts]
 * @returns {Promise<object>} { definition, validation? } for one version, or { definitionId, versions }
 */
export async function readWorkflowDefinition(definitionId, version, tenantId, opts = {}) {
  return apiGet(
    '/api/workflow-definitions-read',
    {
      ...tenantScope(tenantId, opts),
      definitionId,
      ...(version ? { version: String(version) } : {}),
    },
    opts
  );
}

/** @returns {Promise<{ definition: object, rowVersion: string, validation: object }>} */
export async function createWorkflowDraft(definition) {
  return apiPost('/api/workflow-definitions-write', { action: 'create-draft', definition });
}

/**
 * @throws ApiError 409 (write-conflict, version-immutable) when the draft changed or was published
 */
export async function updateWorkflowDraft(definitionId, version, rowVersion, definition) {
  return apiPost('/api/workflow-definitions-write', {
    action: 'update-draft',
    definitionId,
    version,
    rowVersion,
    definition,
  });
}

/** Start the next draft of a published definition. @throws ApiError 409 draft-exists */
export async function newWorkflowVersion(definitionId) {
  return apiPost('/api/workflow-definitions-write', { action: 'new-version', definitionId });
}

/** @throws ApiError 422 definition-invalid (with `errors`), 409 write-conflict */
export async function publishWorkflowDraft(definitionId, version, rowVersion) {
  return apiPost('/api/workflow-definitions-write', {
    action: 'publish',
    definitionId,
    version,
    rowVersion,
  });
}

/** Retire the published version; in-flight runs continue on their snapshot. */
export async function retireWorkflowDefinition(definitionId, version, rowVersion, reason) {
  return apiPost('/api/workflow-definitions-write', {
    action: 'retire',
    definitionId,
    version,
    rowVersion,
    reason,
  });
}

/**
 * @param {{ definitionId?: string, status?: 'running'|'completed'|'cancelled' }} [filter]
 * @param {string} [tenantId] only sent when `opts.env` is set
 * @param {EnvOptions} [opts]
 * @returns {Promise<{ runs: object[] }>} newest first, without step instances
 */
export async function listWorkflowRuns(filter = {}, tenantId, opts = {}) {
  const params = { ...tenantScope(tenantId, opts) };
  if (filter.definitionId) params.definitionId = filter.definitionId;
  if (filter.status) params.status = filter.status;
  return apiGet('/api/workflow-runs-read', params, opts);
}

/**
 * PDWB-03 — the runs still open, counted per workflow, on every plan (WF-B-010):
 * the management-review input of a tenant whose plan no longer has workflows.
 * @returns {Promise<{ running: number, workflows: object[], truncated: boolean }>}
 */
export async function listFinishingWorkflowRuns() {
  return apiGet('/api/workflow-runs-read', { view: 'finishing' });
}

/**
 * @returns {Promise<{ run: object, stepInstances: object[], evidence: object[] }>}
 */
export async function readWorkflowRun(runId, tenantId, opts = {}) {
  return apiGet('/api/workflow-runs-read', { ...tenantScope(tenantId, opts), runId }, opts);
}

// ── Workflow steps (PDWA-04) ────────────────────────────────────────────────
//
// function-contracts/workflow-steps-write.json and workflow-runs-read.json.
// A step is completed only through its task's command surface — this call —
// and the server advances the run inline. A tenant user's own call, so no
// env: a super-admin JWT is refused by the endpoint.

/**
 * The open human workflow steps the caller carries (plus, for an admin, the
 * unassigned ones) — the `workflow-step` task source of collectTaskSources.
 * @returns {Promise<{ steps: object[] }>}
 */
export async function listMyOpenWorkflowSteps() {
  return apiGet('/api/workflow-runs-read', { view: 'open-steps' });
}

/**
 * Every open human workflow step of the tenant (GH #7583) — admin / manager
 * (or a super-admin naming the tenant) only; the workflow-step part of the
 * tenant's open work. STG-B-002: `opts.env` routes cross-environment and
 * names the tenant, which a super-admin JWT does not carry.
 * @param {string} [tenantId]
 * @param {EnvOptions} [opts]
 * @returns {Promise<{ steps: object[], unreadable: number, truncated: boolean }>}
 */
export async function listTenantOpenWorkflowSteps(tenantId, opts = {}) {
  return apiGet(
    '/api/workflow-runs-read',
    { view: 'tenant-open-steps', ...tenantScope(tenantId, opts) },
    opts
  );
}

// ── Halted-workflow reviews (PDW-HALT-2) ─────────────────────────────────────
//
// docs/workflow-halt-review-design.md §3-§4; function-contracts/
// workflow-runs-read.json and workflow-halt-reviews-write.json. Neither
// endpoint checks the plan or the workflows release flag (RFLG-B-006,
// WF-B-010): the review exists on exactly the tenants those switched off.

/**
 * The open reviews of halted recurring workflows the caller carries (plus, for
 * an admin, the unassigned ones) — the `workflow-halt-review` task source of
 * collectTaskSources. Every cause; each review carries its definition's
 * re-enable request state (resumeRequestState) — WF-B-011.
 * @returns {Promise<{ reviews: object[], truncated: boolean }>}
 */
export async function listMyHaltReviews() {
  return apiGet('/api/workflow-runs-read', { view: 'halt-reviews' });
}

/**
 * Every current halt review of the tenant — per definition its open review and
 * its latest closed one, every cause (admin / manager, or a super-admin naming
 * the tenant). The management-review halted-definitions input, and the
 * halt-review part of the tenant's open work. STG-B-002: `opts.env` routes
 * cross-environment and names the tenant.
 * @param {string} [tenantId]
 * @param {EnvOptions} [opts]
 * @returns {Promise<{ reviews: object[], truncated: boolean }>}
 */
export async function listTenantHaltReviews(tenantId, opts = {}) {
  return apiGet(
    '/api/workflow-runs-read',
    { view: 'tenant-halt-reviews', ...tenantScope(tenantId, opts) },
    opts
  );
}

/**
 * Decide a halt review: Retire, Accept or (release-held only) Re-enable
 * requested, with a reason (10-500 chars). `supersede: true` with the landed
 * rowVersion records this decision over one another person landed first
 * (design §6) — never over a landed Retire. `seen` is what the caller read of
 * the landed row ({ decision, decidedAt, resumeRequest }), so a supersede whose
 * row moved only in fields the tenant does not own is re-applied.
 * @param {{ reviewId: string, rowVersion: string, decision: 'retire'|'accept'|'reenable-requested', reason: string, supersede?: boolean, seen?: object }} d
 * @throws ApiError 409 { field: 'decision' | 'resumeRequest', yours, landed, rowVersion } on a clash (responseBody)
 * @returns {Promise<{ ok: true, review: object, superseded: object|null, resumeRequest: object|null }>}
 */
export async function decideHaltReview({
  reviewId,
  rowVersion,
  decision,
  reason,
  supersede = false,
  seen = null,
}) {
  return apiPost('/api/workflow-halt-reviews-write', {
    action: 'decide',
    reviewId,
    rowVersion,
    decision,
    reason,
    ...(supersede ? { supersede: true } : {}),
    ...(seen ? { seen } : {}),
  });
}

/**
 * GH #7583 — the tenant-wide count of open work, the one source of every
 * tenant-wide task figure. { status: 'ok', scope, tasks, questions?, byAssignee? }
 * or { status: 'unknown', missing }. STG-B-002: `opts.env` routes
 * cross-environment and names the tenant for a super-admin caller.
 * @param {string} [tenantId]
 * @param {EnvOptions} [opts]
 */
export async function readTenantTaskSummary(tenantId, opts = {}) {
  return apiGet('/api/task-summary-read', tenantScope(tenantId, opts), opts);
}

/**
 * @param {{ runId: string, stepId: string, rowVersion: string, payload: object }} step
 * @throws ApiError 400 payload-invalid / artefact-unresolved, 403 not-assignee, 409 four-eyes / step-not-open / write-conflict
 */
export async function completeWorkflowStep({ runId, stepId, rowVersion, payload }) {
  return apiPost('/api/workflow-steps-write', {
    action: 'complete-step',
    runId,
    stepId,
    rowVersion,
    payload,
  });
}

/** Move an open step to another active user, with a reason. */
export async function reassignWorkflowStep({ runId, stepId, rowVersion, toUserId, reason }) {
  return apiPost('/api/workflow-steps-write', {
    action: 'reassign-step',
    runId,
    stepId,
    rowVersion,
    toUserId,
    reason,
  });
}

/** Start a run of a published, manual-trigger definition. */
export async function startWorkflowRun(definitionId) {
  return apiPost('/api/workflow-steps-write', { action: 'start-run', definitionId });
}

/** Cancel a running run (admin), with a reason; every open step takes it. */
export async function cancelWorkflowRun(runId, rowVersion, reason) {
  return apiPost('/api/workflow-steps-write', { action: 'cancel-run', runId, rowVersion, reason });
}

// ── Gap-assessment report generation (gap-report-jobs, FV4A-06) ────────────

/**
 * Ask the server to generate the assessment's report in the background. The
 * draft lands on the assessment in tenant state when the job finishes.
 * @param {string} assessmentId
 * @param {string} language  report language code
 * @returns {Promise<{ job: object, created: boolean }>} created false: a job
 *   was already running for this assessment and the caller joined it
 */
export async function startGapReportJob(assessmentId, language) {
  return azurePost('/api/gap-report-jobs?action=start', {
    action: 'start',
    assessmentId,
    language,
  });
}

/** @returns {Promise<{ job: object|null }>} the assessment's most recent report job */
export async function readGapReportJob(assessmentId) {
  return azureGet('/api/gap-report-jobs', { assessmentId });
}

/**
 * One act on a gap-assessment report — review, clear-review, issue,
 * link-document or record-document-score (FV4A-07). The report lives in its
 * own row; the server applies the act to the report as stored and checks its
 * gates there.
 * @returns {Promise<{ report: object, reportRowVersion: string }>}
 * @throws ApiError 409 { code, report, reportRowVersion } when a gate refused
 *   it or it clashed with another act (a new draft, someone else's review)
 */
export async function writeGapReport(assessmentId, action, fields = {}) {
  return azurePost('/api/gap-reports-write', { ...fields, assessmentId, action });
}

// ── Document scan for unmapped controls (control-doc-scans) ──────────────────

/**
 * Start a background scan of the published documents for text that covers
 * these controls. The server resolves the control text itself.
 * @param {string[]} controlIds
 * @returns {Promise<{ scan: object, unresolved: string[] }>}
 * @throws ApiError 409 { code: 'SCAN_ACTIVE' } when a scan is already running
 */
export async function startControlDocScan(controlIds) {
  return azurePost('/api/control-doc-scans?action=start', { action: 'start', controlIds });
}

/** @returns {Promise<{ scan: object }>} one scan with its suggestions */
export async function readControlDocScan(id) {
  return azureGet('/api/control-doc-scans', { id });
}

/**
 * Accept or reject one suggestion. An accept links the control to the
 * document on the server, confirmed by the signed-in user.
 * @param {{ id: string, suggestionId: string, decision: 'accepted'|'rejected', rowVersion: string }} args
 * @returns {Promise<{ scan: object, document: object|null }>}
 * @throws ApiError 409 when the scan or the document changed, or the passage is gone
 */
export async function reviewControlDocScanSuggestion({ id, suggestionId, decision, rowVersion }) {
  return azurePost('/api/control-doc-scans?action=review', {
    action: 'review',
    id,
    suggestionId,
    decision,
    rowVersion,
  });
}
